import type {
  Account,
  AvailableMonthsResponse,
  InsightsSummaryResponse,
  RailInsightsResponse,
  TransactionItem,
  TrendsResponse,
} from '../api/types.js';
import type { CurrentUser } from '../auth/me-fetcher.js';

export const demoUser: CurrentUser = {
  id: 'demo-user',
  email: 'demo@financialcompass.local',
  name: 'Demo Visitor',
  picture_url: null,
  role: 'user',
  csrf_token: 'demo-csrf-token',
  timezone: 'America/Los_Angeles',
  // Demo mode does not include Briefing (no per-user briefings table to
  // read); lt_4mo hides the tab via the header's cohort gate so the demo
  // user never sees a tab that would mount a blank view.
  cohort: 'lt_4mo',
};

export const demoMonths: AvailableMonthsResponse = {
  months: [
    '2026-04',
    '2026-03',
    '2026-02',
    '2026-01',
    '2025-12',
    '2025-11',
    '2025-10',
    '2025-09',
    '2025-08',
    '2025-07',
    '2025-06',
    '2025-05',
  ],
};

export const demoAccounts: Account[] = [
  {
    id: 'demo-checking',
    item_id: 'demo-item-chase',
    plaid_account_id: 'demo-plaid-checking',
    name: 'Everyday Checking',
    official_name: 'Demo Everyday Checking',
    type: 'depository',
    subtype: 'checking',
    mask: '1284',
    current_balance: 8420,
    available_balance: 8420,
    iso_currency_code: 'USD',
    institution_name: 'Demo Chase',
    last_balance_refresh: '2026-05-03T05:00:00Z',
    item_status: 'healthy',
  },
  {
    id: 'demo-credit',
    item_id: 'demo-item-chase',
    plaid_account_id: 'demo-plaid-credit',
    name: 'Freedom Card',
    official_name: 'Demo Freedom Card',
    type: 'credit',
    subtype: 'credit card',
    mask: '4021',
    current_balance: -1280,
    available_balance: 8720,
    iso_currency_code: 'USD',
    institution_name: 'Demo Chase',
    last_balance_refresh: '2026-05-03T05:00:00Z',
    item_status: 'healthy',
  },
  {
    id: 'demo-savings',
    item_id: 'demo-item-boa',
    plaid_account_id: 'demo-plaid-savings',
    name: 'Reserve Savings',
    official_name: 'Demo Reserve Savings',
    type: 'depository',
    subtype: 'savings',
    mask: '7712',
    current_balance: 24200,
    available_balance: 24200,
    iso_currency_code: 'USD',
    institution_name: 'Demo Bank of America',
    last_balance_refresh: '2026-05-03T05:00:00Z',
    item_status: 'healthy',
  },
];

export const demoInsights: InsightsSummaryResponse = {
  month: '2026-04',
  is_partial_month: false,
  income: 10500,
  // DEC-13: day-to-day = Essentials 4050 + Lifestyle 2100 (Wealth 1800 is invested only).
  spending: 6150,
  invested: 1800,
  net_cash_flow: 4350,
  cash_flow_status: 'positive',
  savings_rate: 41,
  big_picture_narrative: {
    summary: 'April was a healthy cash-flow month: income covered regular life, a few fun spikes, and a steady wealth contribution.',
    takeaway: 'The biggest opportunity is not emergency cutting; it is tightening repeat Lifestyle spend while preserving the automatic savings rhythm.',
  },
  categories: [
    {
      bucket: 'Essentials',
      total: 4050,
      percentage: 66,
      change_dollars: -180,
      change_percent: -4.3,
      top_merchants: [
        { name: 'The Park Apartments', total: 2800, count: 1, date: '2026-04-01' },
        { name: 'Whole Foods', total: 410, count: 5 },
        { name: 'PG&E', total: 188, count: 1, date: '2026-04-08' },
      ],
      top_subcategories: [
        {
          name: 'Rent',
          plaid_category: 'Rent',
          total: 2800,
          count: 1,
          percentage: 69,
          prev_month_total: 2800,
          change_dollars: 0,
          change_percent: 0,
          top_merchants: [{ name: 'The Park Apartments', total: 2800, count: 1, date: '2026-04-01' }],
          flags: [],
        },
        {
          name: 'Groceries',
          plaid_category: 'Groceries',
          total: 620,
          count: 8,
          percentage: 15,
          prev_month_total: 740,
          change_dollars: -120,
          change_percent: -16.2,
          top_merchants: [
            { name: 'Whole Foods', total: 410, count: 5 },
            { name: "Trader Joe's", total: 210, count: 3 },
          ],
          flags: [],
        },
        {
          name: 'Utilities',
          plaid_category: 'Utilities',
          total: 310,
          count: 3,
          percentage: 8,
          prev_month_total: 352,
          change_dollars: -42,
          change_percent: -11.9,
          top_merchants: [
            { name: 'PG&E', total: 188, count: 1, date: '2026-04-08' },
            { name: 'Xfinity', total: 122, count: 1, date: '2026-04-12' },
          ],
          flags: [],
        },
      ],
      flags: [],
      narrative: {
        summary: 'Essentials were a little lighter than March, mainly because groceries cooled down.',
        callouts: [
          { label: 'Groceries', type: 'subcategory', detail: 'down $120 from March' },
          { label: 'Rent', type: 'subcategory', detail: 'steady at $2,800' },
        ],
        takeaway: 'The necessities are stable; no obvious stress signal here.',
      },
    },
    {
      bucket: 'Lifestyle',
      total: 2100,
      percentage: 34,
      change_dollars: 360,
      change_percent: 20.7,
      top_merchants: [
        { name: 'DoorDash', total: 287, count: 7 },
        { name: 'Claude.ai', total: 100, count: 1, date: '2026-04-03' },
        { name: 'United Airlines', total: 520, count: 1, date: '2026-04-17' },
      ],
      top_subcategories: [
        {
          name: 'Dining Out',
          plaid_category: 'Dining Out',
          total: 740,
          count: 13,
          percentage: 35,
          prev_month_total: 540,
          change_dollars: 200,
          change_percent: 37,
          top_merchants: [
            { name: 'DoorDash', total: 287, count: 7 },
            { name: 'Sweetgreen', total: 96, count: 2 },
            { name: 'Chipotle', total: 74, count: 2 },
          ],
          flags: [],
        },
        {
          name: 'Travel',
          plaid_category: 'Travel',
          total: 520,
          count: 1,
          percentage: 25,
          prev_month_total: 0,
          change_dollars: 520,
          change_percent: null,
          top_merchants: [{ name: 'United Airlines', total: 520, count: 1, date: '2026-04-17' }],
          flags: [],
        },
        {
          name: 'Software & Tools',
          plaid_category: 'Software & Tools',
          total: 415,
          count: 5,
          percentage: 20,
          prev_month_total: 315,
          change_dollars: 100,
          change_percent: 31.7,
          top_merchants: [
            { name: 'Claude.ai', total: 100, count: 1, date: '2026-04-03' },
            { name: 'Adobe', total: 60, count: 1, date: '2026-04-06' },
            { name: 'Figma', total: 45, count: 1, date: '2026-04-09' },
          ],
          flags: [],
        },
      ],
      flags: [],
      narrative: {
        summary: 'Lifestyle rose in April, mostly from delivery meals and one travel purchase.',
        callouts: [
          { label: 'Dining Out', type: 'subcategory', detail: 'up $200 month over month' },
          { label: 'United Airlines', type: 'merchant', detail: 'one-time $520 travel charge' },
        ],
        takeaway: 'The pattern to watch is repeat food delivery, not the one-off flight.',
      },
    },
    {
      bucket: 'Wealth',
      total: 1800,
      percentage: 0,
      change_dollars: 300,
      change_percent: 20,
      top_merchants: [
        { name: 'Vanguard', total: 1200, count: 1, date: '2026-04-05' },
        { name: 'Reserve Savings', total: 600, count: 1, date: '2026-04-20' },
      ],
      top_subcategories: [
        {
          name: 'Investment Contribution',
          plaid_category: 'Investment Contribution',
          total: 1200,
          count: 1,
          percentage: 67,
          prev_month_total: 1000,
          change_dollars: 200,
          change_percent: 20,
          top_merchants: [{ name: 'Vanguard', total: 1200, count: 1, date: '2026-04-05' }],
          flags: [],
        },
        {
          name: 'Savings',
          plaid_category: 'Savings',
          total: 600,
          count: 1,
          percentage: 33,
          prev_month_total: 500,
          change_dollars: 100,
          change_percent: 20,
          top_merchants: [{ name: 'Reserve Savings', total: 600, count: 1, date: '2026-04-20' }],
          flags: [],
        },
      ],
      flags: [],
      narrative: {
        summary: 'Wealth activity stayed strong with $1,800 moved toward future-you.',
        callouts: [
          { label: 'Vanguard', type: 'merchant', detail: '$1,200 contribution' },
          { label: 'Reserve Savings', type: 'merchant', detail: '$600 automatic transfer' },
        ],
        takeaway: 'This is the habit to preserve if Lifestyle needs trimming.',
      },
    },
  ],
  generated_at: '2026-05-03T05:00:00Z',
};

export const demoRailInsights: RailInsightsResponse = {
  month: '2026-04',
  insights: [
    {
      id: 'demo-cash-positive',
      type: 'positive_wins',
      icon: '✓',
      headline: 'April ended cash-flow positive',
      detail: 'Income exceeded day-to-day spending (Essentials + Lifestyle) by $4,350. Wealth stays separate.',
      tier: 1,
      amount: 4350,
    },
    {
      id: 'demo-delivery',
      type: 'high_frequency_merchant',
      icon: '$',
      headline: 'Delivery meals are the main Lifestyle pattern',
      detail: 'DoorDash was $287 across 7 transactions, more than any other dining merchant.',
      tier: 2,
      amount: 287,
    },
    {
      id: 'demo-subscriptions',
      type: 'subscription_total',
      icon: '↻',
      headline: 'Subscriptions total about $224/month',
      detail: 'Software and entertainment streams are useful, but a few are worth reviewing before launch.',
      tier: 2,
      amount: 224,
    },
    {
      id: 'demo-travel',
      type: 'one_time_charge',
      icon: '!',
      headline: 'One travel purchase explains a quarter of Lifestyle',
      detail: 'United Airlines added a one-time $520 charge on April 17.',
      tier: 3,
      amount: 520,
    },
  ],
  total_count: 4,
  generated_at: '2026-05-03T05:00:00Z',
};

// Trends `total` = Essentials + Lifestyle (DEC-14 day-to-day); wealth stays a field.
export const demoTrends: TrendsResponse = {
  months: [
    { month: '2025-05', essentials: 3820, lifestyle: 1780, wealth: 1100, uncategorized: 0, total: 5600 },
    { month: '2025-06', essentials: 3910, lifestyle: 1840, wealth: 1200, uncategorized: 0, total: 5750 },
    { month: '2025-07', essentials: 3880, lifestyle: 1950, wealth: 1000, uncategorized: 0, total: 5830 },
    { month: '2025-08', essentials: 4020, lifestyle: 1720, wealth: 1300, uncategorized: 0, total: 5740 },
    { month: '2025-09', essentials: 3970, lifestyle: 1810, wealth: 1300, uncategorized: 0, total: 5780 },
    { month: '2025-10', essentials: 4015, lifestyle: 2050, wealth: 1400, uncategorized: 0, total: 6065 },
    { month: '2025-11', essentials: 4120, lifestyle: 1980, wealth: 1400, uncategorized: 0, total: 6100 },
    { month: '2025-12', essentials: 4230, lifestyle: 2440, wealth: 900, uncategorized: 0, total: 6670 },
    { month: '2026-01', essentials: 4090, lifestyle: 1880, wealth: 1500, uncategorized: 0, total: 5970 },
    { month: '2026-02', essentials: 4170, lifestyle: 1690, wealth: 1500, uncategorized: 0, total: 5860 },
    { month: '2026-03', essentials: 4230, lifestyle: 1740, wealth: 1500, uncategorized: 0, total: 5970 },
    { month: '2026-04', essentials: 4050, lifestyle: 2100, wealth: 1800, uncategorized: 0, total: 6150 },
  ],
};

const baseTransaction = {
  authorized_date: null,
  currency: 'USD',
  category_confidence: 'HIGH',
  is_transfer: false,
  transfer_type: null,
  is_recurring: false,
  pending: false,
  payment_channel: 'online',
  location_city: null,
  location_region: null,
  logo_url: null,
  merchant_entity_id: null,
  account_id: 'demo-checking',
  account_name: 'Everyday Checking',
  account_type: 'depository',
  institution_name: 'Demo Chase',
} satisfies Omit<TransactionItem, 'id' | 'date' | 'name' | 'merchant_name' | 'amount' | 'personal_finance_category' | 'bucket'>;

export const demoTransactions: TransactionItem[] = [
  tx('demo-tx-rent', '2026-04-01', 'The Park Apartments', -2800, 'Essentials', 'Rent'),
  tx('demo-tx-vanguard', '2026-04-05', 'Vanguard', -1200, 'Wealth', 'Investment Contribution'),
  tx('demo-tx-savings', '2026-04-20', 'Reserve Savings', -600, 'Wealth', 'Savings'),
  tx('demo-tx-pge', '2026-04-08', 'PG&E', -188, 'Essentials', 'Utilities'),
  tx('demo-tx-xfinity', '2026-04-12', 'Xfinity', -122, 'Essentials', 'Utilities'),
  tx('demo-tx-whole-1', '2026-04-02', 'Whole Foods', -82, 'Essentials', 'Groceries'),
  tx('demo-tx-whole-2', '2026-04-09', 'Whole Foods', -76, 'Essentials', 'Groceries'),
  tx('demo-tx-whole-3', '2026-04-16', 'Whole Foods', -91, 'Essentials', 'Groceries'),
  tx('demo-tx-whole-4', '2026-04-23', 'Whole Foods', -88, 'Essentials', 'Groceries'),
  tx('demo-tx-whole-5', '2026-04-28', 'Whole Foods', -73, 'Essentials', 'Groceries'),
  tx('demo-tx-trader-1', '2026-04-04', "Trader Joe's", -72, 'Essentials', 'Groceries'),
  tx('demo-tx-trader-2', '2026-04-14', "Trader Joe's", -68, 'Essentials', 'Groceries'),
  tx('demo-tx-trader-3', '2026-04-25', "Trader Joe's", -70, 'Essentials', 'Groceries'),
  tx('demo-tx-doordash-1', '2026-04-03', 'DoorDash', -41, 'Lifestyle', 'Dining Out'),
  tx('demo-tx-doordash-2', '2026-04-06', 'DoorDash', -38, 'Lifestyle', 'Dining Out'),
  tx('demo-tx-doordash-3', '2026-04-10', 'DoorDash', -44, 'Lifestyle', 'Dining Out'),
  tx('demo-tx-doordash-4', '2026-04-13', 'DoorDash', -37, 'Lifestyle', 'Dining Out'),
  tx('demo-tx-doordash-5', '2026-04-19', 'DoorDash', -43, 'Lifestyle', 'Dining Out'),
  tx('demo-tx-doordash-6', '2026-04-22', 'DoorDash', -39, 'Lifestyle', 'Dining Out'),
  tx('demo-tx-doordash-7', '2026-04-27', 'DoorDash', -45, 'Lifestyle', 'Dining Out'),
  tx('demo-tx-sweetgreen-1', '2026-04-11', 'Sweetgreen', -48, 'Lifestyle', 'Dining Out'),
  tx('demo-tx-sweetgreen-2', '2026-04-24', 'Sweetgreen', -48, 'Lifestyle', 'Dining Out'),
  tx('demo-tx-chipotle-1', '2026-04-05', 'Chipotle', -36, 'Lifestyle', 'Dining Out'),
  tx('demo-tx-chipotle-2', '2026-04-18', 'Chipotle', -38, 'Lifestyle', 'Dining Out'),
  tx('demo-tx-united', '2026-04-17', 'United Airlines', -520, 'Lifestyle', 'Travel'),
  tx('demo-tx-claude', '2026-04-03', 'Claude.ai', -100, 'Lifestyle', 'Software & Tools'),
  tx('demo-tx-adobe', '2026-04-06', 'Adobe', -60, 'Lifestyle', 'Software & Tools'),
  tx('demo-tx-figma', '2026-04-09', 'Figma', -45, 'Lifestyle', 'Software & Tools'),
  tx('demo-tx-income', '2026-04-15', 'Acme Payroll', 10500, 'Income', 'Paycheck'),
];

function tx(
  id: string,
  date: string,
  merchant: string,
  amount: number,
  bucket: string,
  pfc: string,
): TransactionItem {
  return {
    ...baseTransaction,
    id,
    date,
    name: merchant,
    merchant_name: merchant,
    amount,
    personal_finance_category: pfc,
    bucket,
    subcategory: pfc,
  };
}
